import Link from 'next/link'
import styled from 'styled-components'

import { colors } from '../../constants/colors'

export type HomeMobileSectionKind =
  | 'accessories'
  | 'animals'
  | 'business'
  | 'car'
  | 'children'
  | 'clothes'
  | 'creativity'
  | 'digital'
  | 'entertainment'
  | 'health'
  | 'home'
  | 'other'
  | 'people'
  | 'photo'
  | 'realEstate'
  | 'special'
  | 'sport'
  | 'tech'
  | 'tools'
  | 'travel'
  | 'water'

interface Position {
  height: number
  left?: number
  right?: number
  top: number
  transform?: string
  width: number
}

interface ImagePosition extends Position {
  src: string
}

interface BubblePosition extends Position {
  background: string
  radius?: number
}

interface MobileSectionConfig {
  background: string
  bubble?: BubblePosition
  layers: ImagePosition[]
  width: number
}

interface HomeMobileSectionItemProps {
  kind: HomeMobileSectionKind
  title: string
  to: string
}

const assetRoot = '/images/home-sections'
const mobileAssetRoot = `${assetRoot}/mobile`

const configs: Record<HomeMobileSectionKind, MobileSectionConfig> = {
  accessories: {
    background: '#fffbe3',
    bubble: {
      background: '#fef5ab',
      height: 72,
      radius: 94,
      right: -11,
      top: 32,
      transform: 'rotate(-10.74deg)',
      width: 72,
    },
    layers: [
      {
        height: 54,
        right: 1,
        src: `${mobileAssetRoot}/necklace.png`,
        top: 25,
        transform: 'rotate(13.53deg)',
        width: 81,
      },
      { height: 27, right: 20, src: `${mobileAssetRoot}/ring.png`, top: 29, width: 27 },
    ],
    width: 102,
  },
  animals: {
    background: '#fff2e3',
    bubble: {
      background: '#feceab',
      height: 63,
      left: 32,
      radius: 26,
      top: 29,
      transform: 'rotate(-10.74deg)',
      width: 63,
    },
    layers: [{ height: 67, left: 34, src: `${assetRoot}/dog.png`, top: 21, width: 45 }],
    width: 97,
  },
  business: {
    background: '#eae3ff',
    bubble: {
      background: '#c6abfe',
      height: 76,
      radius: 94,
      right: -11,
      top: 27,
      transform: 'rotate(-10.74deg)',
      width: 76,
    },
    layers: [{ height: 59, right: 2, src: `${mobileAssetRoot}/business.png`, top: 20, width: 64 }],
    width: 102,
  },
  car: {
    background: '#e2ecff',
    layers: [
      { height: 86, right: -13, src: `${mobileAssetRoot}/car-shape.svg`, top: 19, width: 115 },
      { height: 62, right: -1, src: `${assetRoot}/car.png`, top: 13, width: 91 },
    ],
    width: 97,
  },
  children: {
    background: '#ffe3f4',
    bubble: {
      background: '#feabce',
      height: 87,
      radius: 94,
      right: -17,
      top: 25,
      transform: 'rotate(-10.74deg)',
      width: 87,
    },
    layers: [{ height: 60, right: 18, src: `${mobileAssetRoot}/children.png`, top: 24, width: 44 }],
    width: 109,
  },
  clothes: {
    background: '#ffe2f2',
    bubble: {
      background: '#ffcde6',
      height: 44,
      radius: 19,
      right: -1,
      top: 30,
      transform: 'rotate(-10.74deg)',
      width: 44,
    },
    layers: [
      { height: 70, right: 22, src: `${assetRoot}/shirt.png`, top: 31, width: 43 },
      { height: 64, right: 2, src: `${assetRoot}/man.png`, top: 21, width: 32 },
    ],
    width: 97,
  },
  creativity: {
    background: '#f4ffe3',
    bubble: {
      background: '#f2feab',
      height: 80,
      radius: 94,
      right: -21,
      top: 22,
      transform: 'rotate(-10.74deg)',
      width: 80,
    },
    layers: [
      { height: 72, right: 2, src: `${mobileAssetRoot}/creativity.png`, top: 20, width: 72 },
    ],
    width: 102,
  },
  digital: {
    background: '#fad0ff',
    bubble: {
      background: '#fd9ffd',
      height: 59,
      radius: 23,
      right: 26,
      top: 33,
      transform: 'rotate(-10.74deg)',
      width: 59,
    },
    layers: [
      {
        height: 59,
        right: 21,
        src: `${assetRoot}/phone.png`,
        top: 23,
        transform: 'rotate(7.58deg)',
        width: 59,
      },
    ],
    width: 115,
  },
  entertainment: {
    background: '#e3fff3',
    bubble: {
      background: '#abfeea',
      height: 70,
      radius: 94,
      right: -6,
      top: 20,
      transform: 'rotate(-10.74deg)',
      width: 70,
    },
    layers: [
      { height: 55, right: -12, src: `${mobileAssetRoot}/entertainment.png`, top: 27, width: 109 },
    ],
    width: 102,
  },
  health: {
    background: '#eee3ff',
    bubble: {
      background: '#d9abfe',
      height: 72,
      radius: 94,
      right: -13,
      top: 20,
      transform: 'rotate(-10.74deg)',
      width: 72,
    },
    layers: [{ height: 73, right: -10, src: `${mobileAssetRoot}/health.png`, top: 22, width: 105 }],
    width: 102,
  },
  home: {
    background: '#e3fcff',
    bubble: {
      background: '#abc7fe',
      height: 73,
      left: 27,
      radius: 94,
      top: 23,
      transform: 'rotate(-10.74deg)',
      width: 73,
    },
    layers: [{ height: 76, left: 47, src: `${assetRoot}/chair.png`, top: -6, width: 39 }],
    width: 102,
  },
  other: {
    background: '#e3ffe7',
    bubble: {
      background: '#abfec2',
      height: 65,
      radius: 94,
      right: -8,
      top: 18,
      transform: 'rotate(-10.74deg)',
      width: 65,
    },
    layers: [{ height: 59, right: 5, src: `${mobileAssetRoot}/other.png`, top: 13, width: 59 }],
    width: 97,
  },
  people: {
    background: '#fffbe3',
    bubble: {
      background: '#fef5ab',
      height: 72,
      radius: 26,
      right: -23,
      top: 21,
      transform: 'rotate(-10.74deg)',
      width: 72,
    },
    layers: [{ height: 69, right: 1, src: `${assetRoot}/people.png`, top: 13, width: 69 }],
    width: 97,
  },
  photo: {
    background: '#e3e3ff',
    bubble: {
      background: '#abb8fe',
      height: 77,
      radius: 94,
      right: -10,
      top: 17,
      transform: 'rotate(-10.74deg)',
      width: 77,
    },
    layers: [{ height: 45, right: 15, src: `${mobileAssetRoot}/camera.png`, top: 25, width: 45 }],
    width: 102,
  },
  realEstate: {
    background: '#fff1e0',
    bubble: {
      background: '#ffe6c7',
      height: 101,
      radius: 106,
      right: -18,
      top: 20,
      transform: 'rotate(-10.74deg)',
      width: 101,
    },
    layers: [{ height: 61, right: -15, src: `${assetRoot}/real-estate.png`, top: 25, width: 100 }],
    width: 97,
  },
  special: {
    background: '#ffefd0',
    bubble: {
      background: '#fdcd9f',
      height: 66,
      radius: 23,
      right: 11,
      top: 29,
      transform: 'rotate(-10.74deg)',
      width: 66,
    },
    layers: [{ height: 66, right: 17, src: `${assetRoot}/bulldozer.png`, top: 13, width: 82 }],
    width: 115,
  },
  sport: {
    background: '#ffebe3',
    bubble: {
      background: '#fec4ab',
      height: 64,
      radius: 94,
      right: -7,
      top: 10,
      transform: 'rotate(-10.74deg)',
      width: 64,
    },
    layers: [
      {
        height: 63,
        right: 9,
        src: `${mobileAssetRoot}/sport.png`,
        top: 8,
        transform: 'rotate(16.77deg)',
        width: 60,
      },
      { height: 17, right: 8, src: `${mobileAssetRoot}/ball.png`, top: 34, width: 18 },
    ],
    width: 97,
  },
  tech: {
    background: '#ffd0d0',
    bubble: {
      background: '#fd9f9f',
      height: 70,
      radius: 23,
      right: -4,
      top: 25,
      transform: 'rotate(-10.74deg)',
      width: 70,
    },
    layers: [
      {
        height: 63,
        right: -4,
        src: `${assetRoot}/motorcycle.png`,
        top: 22,
        transform: 'rotate(7.75deg)',
        width: 89,
      },
    ],
    width: 115,
  },
  tools: {
    background: '#c2fbff',
    bubble: {
      background: '#a3eaf9',
      height: 54,
      left: 41,
      radius: 72,
      top: 26,
      transform: 'rotate(-10.74deg)',
      width: 54,
    },
    layers: [
      {
        height: 61,
        left: 26,
        src: `${assetRoot}/tools.png`,
        top: 13,
        transform: 'rotate(11.35deg)',
        width: 60,
      },
    ],
    width: 97,
  },
  travel: {
    background: '#e5ffe3',
    bubble: {
      background: '#abfeae',
      height: 80,
      radius: 94,
      right: -17,
      top: 20,
      transform: 'rotate(-10.74deg)',
      width: 80,
    },
    layers: [
      { height: 134, right: -13, src: `${mobileAssetRoot}/travel.png`, top: -16, width: 107 },
    ],
    width: 109,
  },
  water: {
    background: '#d0e6ff',
    bubble: {
      background: '#9fb4fd',
      height: 57,
      radius: 102,
      right: -2,
      top: 20,
      transform: 'rotate(-10.74deg)',
      width: 57,
    },
    layers: [
      { height: 17, right: 8, src: `${mobileAssetRoot}/helicopter.png`, top: 23, width: 47 },
      { height: 45, right: -12, src: `${mobileAssetRoot}/yacht.png`, top: 38, width: 107 },
    ],
    width: 137,
  },
}

export function HomeMobileSectionItem({ kind, title, to }: HomeMobileSectionItemProps) {
  const config = configs[kind]

  return (
    <Card $background={config.background} $width={config.width} href={to}>
      {config.bubble && (
        <Bubble
          style={{
            ...getPositionStyle(config.bubble),
            background: config.bubble.background,
            borderRadius: config.bubble.radius,
          }}
        />
      )}
      {config.layers.map((layer, index) => (
        <ImageLayer
          alt={''}
          key={`${layer.src}-${index}`}
          src={layer.src}
          style={getPositionStyle(layer)}
        />
      ))}
      <Title>{title}</Title>
    </Card>
  )
}

function getPositionStyle(position: Position) {
  return {
    height: position.height,
    left: position.left,
    right: position.right,
    top: position.top,
    transform: position.transform,
    width: position.width,
  }
}

const Card = styled(Link)<{ $background: string; $width: number }>`
  position: relative;
  flex: 0 0 ${({ $width }) => $width}px;
  width: ${({ $width }) => $width}px;
  height: 71px;
  overflow: hidden;
  border-radius: 12px;
  background: ${({ $background }) => $background};
  color: ${colors.black};
  text-decoration: none;
`

const Title = styled.span`
  position: absolute;
  z-index: 3;
  top: 6px;
  left: 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.5;
  white-space: pre-line;
`

const Bubble = styled.div`
  position: absolute;
  border-radius: 94px;
`

const ImageLayer = styled.img`
  position: absolute;
  object-fit: cover;
  pointer-events: none;
`
